import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useGetMetricsQuery, useGetGroupMetricsQuery } from '../../api/metricsApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import { CHANNEL_DISPLAY } from '../../utils/channels';
import { buildRangeQuery } from '../../utils/dateRange';
import RangeTabs from '../../components/RangeTabs/RangeTabs';
import DeliveryStatsCell from '../../components/DeliveryStatsCell/DeliveryStatsCell';
import s from '../../styles/shared/page.module.css';
import table from '../../styles/shared/table.module.css';

const RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'week' },
  { label: 'This month', value: 'month' },
  { label: 'Custom', value: 'custom' },
];

const DETAILS_CHANNELS = new Set(['email', 'text', 'telegram']);

function toRangeParam(rangeKey, customDates) {
  if (rangeKey !== 'custom') return { key: rangeKey };
  const [from, to] = customDates ?? [];
  if (!from || !to) return null;
  const endOfDay = new Date(to);
  endOfDay.setHours(23, 59, 59, 999);
  return { key: 'custom', from: from.toISOString(), to: endOfDay.toISOString() };
}

function buildDeliveriesUrl(groupId, channel, range) {
  return `/manager/metrics/groups/${groupId}/deliveries?channel=${encodeURIComponent(channel)}&${buildRangeQuery(range)}`;
}

function buildSubscribersUrl(groupId, range) {
  return `/manager/metrics/groups/${groupId}/subscribers?${buildRangeQuery(range)}`;
}

function ChannelCell({ channel, group, onOpenDetails }) {
  const sent = group.by_channel?.[channel] ?? 0;
  const delivered = group.delivered_by_channel?.[channel] ?? 0;
  const failed = group.failed_by_channel?.[channel] ?? 0;
  const hasDetails = DETAILS_CHANNELS.has(channel) && sent > 0;

  return (
    <DeliveryStatsCell
      sent={sent}
      delivered={delivered}
      failed={failed}
      hasDetails={hasDetails}
      detailsLabel="Delivery details"
      onOpenDetails={() => onOpenDetails(group, channel)}
    />
  );
}

export default function MetricsPage() {
  const navigate = useNavigate();
  const showToast = useToast();
  const overlayRef = useRef(null);
  const [rangeKey, setRangeKey] = useState('today');
  const [customDates, setCustomDates] = useState(null);

  const range = useMemo(() => toRangeParam(rangeKey, customDates), [rangeKey, customDates]);

  const {
    data: metrics,
    isLoading: loadingTotals,
    isError: isTotalsError,
    error: totalsError,
  } = useGetMetricsQuery(undefined, { refetchOnMountOrArgChange: true });

  const {
    data: groupMetrics,
    isLoading: loadingGroups,
    isError: isGroupsError,
    error: groupsError,
  } = useGetGroupMetricsQuery(range, { skip: !range });

  useEffect(() => {
    if (isTotalsError) showToast('error', 'Metrics', errMsg(totalsError, 'Could not load metrics.'));
  }, [isTotalsError, totalsError, showToast]);

  useEffect(() => {
    if (isGroupsError) showToast('error', 'Metrics', errMsg(groupsError, 'Could not load group metrics.'));
  }, [isGroupsError, groupsError, showToast]);

  const totalsRows = metrics
    ? [
        {
          label: 'Subscribers',
          value: metrics.subscribers.total,
          sublabel: `${metrics.subscribers.verified} verified`,
        },
        { label: 'Groups', value: metrics.groups.total },
        { label: 'Hosts', value: metrics.hosts.total },
        { label: 'Giveaways', value: metrics.giveaways.total },
        { label: 'Messages sent', value: metrics.messages.total },
      ]
    : [];

  const groupRows = groupMetrics?.groups ?? [];

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Metrics</h1>
          <p className={`${s.subtitle} textMuted`}>Totals for your organization.</p>
        </div>
      </div>

      <div className={`${table.tableWrapper} ${s.metricsTable}`}>
        <DataTable
          value={totalsRows}
          loading={loadingTotals}
          emptyMessage="No metrics yet."
          responsiveLayout="stack"
        >
          <Column
            field="label"
            header="Metric"
            style={{ width: '320px' }}
            body={(r) => <div className={table.cellInsider}>{r.label}</div>}
          />
          <Column
            header="Value"
            style={{ width: '320px' }}
            body={(r) => (
              <div className={table.cellInsider}>
                <span className={s.metricValue}>{r.value}</span>
                {r.sublabel && <span className={s.metricSublabel}>{r.sublabel}</span>}
              </div>
            )}
          />
        </DataTable>
      </div>

      <div className={s.groupMetricsHeader}>
        <h2>Groups</h2>
        <div className={s.rangeBar}>
          <RangeTabs
            options={RANGE_OPTIONS}
            value={rangeKey}
            onChange={(value, e) => {
              setRangeKey(value);
              if (value === 'custom') {
                overlayRef.current?.toggle(e);
              } else {
                setCustomDates(null);
              }
            }}
          />
          {rangeKey === 'custom' && customDates?.[0] && customDates?.[1] && (
            <span className={s.mutedCell}>
              {customDates[0].toLocaleDateString()} – {customDates[1].toLocaleDateString()}
            </span>
          )}
          <OverlayPanel ref={overlayRef} className={s.rangeOverlay}>
            <Calendar
              selectionMode="range"
              value={customDates}
              onChange={(e) => {
                setCustomDates(e.value);
                if (e.value?.[0] && e.value?.[1]) overlayRef.current?.hide();
              }}
              inline
            />
          </OverlayPanel>
        </div>
      </div>

      <div className={table.tableWrapper}>
        <DataTable
          value={groupRows}
          loading={loadingGroups}
          emptyMessage={range ? 'No groups yet.' : 'Pick a custom date range above.'}
          responsiveLayout="stack"
        >
          <Column
            header="Group"
            className={table.contactCol}
            body={(g) => <div className={table.cellInsider}>{g.name}</div>}
          />
          <Column
            header="Subscribers"
            body={(g) => (
              <div className={table.cellInsider}>
                <div className={s.subscriberCell}>
                  <span>{g.subscriber_count}</span>
                  <button
                    type="button"
                    className={s.chipButton}
                    onClick={() => navigate(buildSubscribersUrl(g.group_id, range))}
                  >
                    <i className="pi pi-users" />
                    View
                  </button>
                </div>
              </div>
            )}
          />
          <Column
            header="Messages sent"
            body={(g) => <div className={table.cellInsider}>{g.messages_sent}</div>}
          />
          {CHANNEL_DISPLAY.map(({ key, label }) => (
            <Column
              key={key}
              header={label}
              body={(g) => (
                <ChannelCell
                  channel={key}
                  group={g}
                  onOpenDetails={(grp, ch) => navigate(buildDeliveriesUrl(grp.group_id, ch, range))}
                />
              )}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
}
